import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button as MuiButton,
  makeStyles,
  Snackbar
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { api } from '@services/api';
import { MagnifyingGlass, X, Calendar, Download } from '@phosphor-icons/react';
import { Pagination } from '@material-ui/lab';
import usePagination from '@hooks/usePagination';
import { situ } from '@data/colab';
import * as XLSX from 'xlsx';

const useStyles = makeStyles((theme) => ({
  primaryButton: {
    backgroundColor: '#007bff',
    color: 'white',
    '&:hover': {
      backgroundColor: '#0056b3',
    },
    '&:disabled': {
      backgroundColor: '#6c757d',
      color: 'white',
    },
  },
  secondaryButton: {
    backgroundColor: '#6c757d',
    color: 'white',
    '&:hover': {
      backgroundColor: '#5a6268',
    },
  },
  redButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    '&:hover': {
      backgroundColor: '#c82333',
    },
  },
  successButton: {
    backgroundColor: '#28a745',
    color: 'white',
    '&:hover': {
      backgroundColor: '#218838',
    },
  },
}));

// 🔥 FUNÇÃO PARA FORMATAR DATA DE FORMA SEGURA
function formatDateBr(dateString: string | Date | null | undefined): string {
  if (!dateString) return '';
  
  try {
    if (dateString instanceof Date) {
      if (isNaN(dateString.getTime())) return '';
      const dia = String(dateString.getDate()).padStart(2, '0');
      const mes = String(dateString.getMonth() + 1).padStart(2, '0');
      const ano = dateString.getFullYear();
      return `${dia}/${mes}/${ano}`;
    }
    
    if (typeof dateString === 'string') {
      const dataLimpa = dateString.trim();
      if (!dataLimpa) return '';
      
      let dia, mes, ano;
      
      if (dataLimpa.match(/^\d{4}-\d{2}-\d{2}/)) {
        const partes = dataLimpa.split('T')[0].split('-');
        ano = partes[0];
        mes = partes[1];
        dia = partes[2];
      }
      else if (dataLimpa.match(/^\d{2}\/\d{2}\/\d{4}/)) {
        const partes = dataLimpa.split('/');
        dia = partes[0];
        mes = partes[1];
        ano = partes[2];
      }
      else if (dataLimpa.match(/^\d{2}-\d{2}-\d{4}/)) {
        const partes = dataLimpa.split('-');
        dia = partes[0];
        mes = partes[1];
        ano = partes[2];
      }
      else {
        const dataObj = new Date(dataLimpa);
        if (!isNaN(dataObj.getTime())) {
          dia = String(dataObj.getDate()).padStart(2, '0');
          mes = String(dataObj.getMonth() + 1).padStart(2, '0');
          ano = dataObj.getFullYear();
        } else {
          return '';
        }
      }
      
      if (dia && mes && ano && dia !== 'NaN' && mes !== 'NaN' && ano !== 'NaN') {
        return `${dia}/${mes}/${ano}`;
      }
      return '';
    }
    
    return '';
  } catch (error) {
    console.error('Erro ao formatar data:', dateString, error);
    return '';
  }
}

// 🔥 FUNÇÃO PARA FORMATAR TELEFONE
function formatTelefone(telefone: string): string {
  if (!telefone) return '';
  const numeros = telefone.replace(/\D/g, '');
  if (numeros.length === 10) {
    return `(${numeros.slice(0,2)}) ${numeros.slice(2,6)}-${numeros.slice(6,10)}`;
  }
  if (numeros.length === 11) {
    return `(${numeros.slice(0,2)}) ${numeros.slice(2,7)}-${numeros.slice(7,11)}`;
  }
  return telefone;
}

// 🔥 COMPONENTE AUTOCOMPLETE SEGURO
function SafeAutocompleteCnpj({ cnpjExcel, selectedCnpj, onChangeSelectedCnpj, onChangeRazaoSocial }: any) {
  
  const empresasUnicas = useMemo(() => {
    const empresasMap = new Map();
    cnpjExcel.forEach((item: any) => {
      if (!empresasMap.has(item.cnpj)) {
        empresasMap.set(item.cnpj, {
          cnpj: item.cnpj,
          razao_social: item.razao_social,
          label: `${item.razao_social} - ${item.cnpj}`
        });
      }
    });
    return Array.from(empresasMap.values());
  }, [cnpjExcel]);

  const valorSelecionado = useMemo(() => {
    if (!selectedCnpj?.cnpj) return null;
    return empresasUnicas.find(e => e.cnpj === selectedCnpj.cnpj) || null;
  }, [selectedCnpj, empresasUnicas]);

  const handleChange = (event: any, newValue: any) => {
    if (!newValue) {
      onChangeSelectedCnpj(null);
      onChangeRazaoSocial('');
    } else {
      onChangeSelectedCnpj({
        cnpj: newValue.cnpj,
        razao_social: newValue.razao_social
      });
      onChangeRazaoSocial(newValue.razao_social);
    }
  };

  return (
    <Autocomplete
      options={empresasUnicas}
      value={valorSelecionado}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Buscar Empresa"
          variant="outlined"
          size="small"
          placeholder="Digite o nome da empresa ou CNPJ..."
        />
      )}
      style={{ minWidth: 400 }}
      clearOnEscape
      freeSolo={false}
      disableClearable={false}
      noOptionsText="Nenhuma empresa encontrada"
    />
  );
}

export interface ExamesAvencerProps {
  nome_completo: string;
  cpf_colaborador: string;
  data_agendamento: string;
  data_vencimento: string;
  data_nascimento: string;
  funcao: string;
  setor: string;
  email: string;
  telefone: string;
  status_colaborador: string; 
  status_agendamento: string;
  tipo_aso: string;
  cnpj: string;
  razao_social: string;
  local_obra: string;
  ghe: string;
  cod_ghe: string;
  nome_exame: string;
  dias: number;
  dias_vencer: number;
  cor: string;
}

function createData(
  nome_comercial: string,
  cpf: string,
  nome: string,
  data: string,
  cnpj: string,
  razao_social: string,
  situacao: string,
  ghe: string,
  local_obra: string
) {
  return {
    nome_comercial,
    cpf,
    nome,
    data,
    cnpj,
    razao_social,
    situacao,
    ghe,
    local_obra
  };
}

function Row(props: { row: ReturnType<typeof createData> }) {
  const { row } = props;
  const [open, setOpen] = useState(false);
  const [exames, setExames] = useState<ExamesAvencerProps[]>([]);
  const [loading, setLoading] = useState(false);

  async function fetchExamesAvencer(cpf_colaborador: string) {
    const data = {
      cpf_colaborador
    };

    const nextOpenState = !open;
    setOpen(nextOpenState);

    try {
      if (nextOpenState && exames.length === 0) {
        setLoading(true);
        const response = await api.post('ravim/exames-vencer', data);
        if (response.data.estado === 404) {
          setExames([]);
          return;
        }
        setExames(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={async () => {
              await fetchExamesAvencer(row.cpf);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.cnpj}
        </TableCell>
        <TableCell align="right">{row.razao_social}</TableCell>
        <TableCell align="right">{row.cpf}</TableCell>
        <TableCell align="right">{row.nome}</TableCell>
        <TableCell align="right">{row.situacao}</TableCell>
        <TableCell align="right">{row.data}</TableCell>
        <TableCell align="right">{row.ghe}</TableCell>
        <TableCell align="right">{row.local_obra}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0, background: '#e3e3e3' }}
          colSpan={9}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom component="div">
                Exames
              </Typography>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell>Exame</TableCell>
                    <TableCell>Data do último exame</TableCell>
                    <TableCell>Data de Vencimento</TableCell>
                    <TableCell>Dias para Vencimento</TableCell>
                  </TableRow>
                </TableHead>
                {loading && (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress />
                  </Box>
                )}
                {exames.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body1">Nenhum exame encontrado</Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableBody>
                  {exames?.map((exame, i) => (
                    <TableRow key={i}>
                      <TableCell component="th" scope="row">
                        {exame.nome_exame}
                      </TableCell>
                      <TableCell>
                        {exame?.data_agendamento
                          ? formatDateBr(exame?.data_agendamento)
                          : ''}
                      </TableCell>
                      <TableCell>
                        {exame?.data_vencimento
                          ? formatDateBr(exame?.data_vencimento)
                          : ''}
                      </TableCell>
                      <TableCell
                        style={{
                          background: exame.cor,
                          fontWeight: 'bold'
                        }}
                      >
                        {exame.dias_vencer}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export interface ColaboradoresProps {
  id_colaboradores: number;
  matricula_empresa: string;
  matricula_esocial: string;
  nome_completo: string;
  cpf_colaborador: string;
  rg: string;
  data_nascimento: string;
  genero: string;
  cnh: string;
  email: string;
  telefone: string;
  situacao: string;
  nis: string;
  funcao: string;
  setor: string;
  turno: string;
  cnpj: string;
  razao_social: string;
  data_admissao: string;
  horario_trab_entrada: string;
  horario_trab_saida: string;
  local_obra: string;
  cod_ghe: string;
  ghe: string;
  exibir: string;
  data_ultimo_agendamento: string;
}

export function ExamesAvencer() {
  const classes = useStyles();
  const [dataUser, setDataUser] = useState<ColaboradoresProps[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCnpjValue, setSelectedCnpjValue] = useState<any>(null);
  const [nomeEmpresa, setNomeEmpresa] = useState<string>('');
  const [cnpjExcel, setCnpjExcel] = useState<ColaboradoresProps[]>([]);
  const [situacao, setSituacao] = useState('');
  const [filterContracts, setFilterContracts] = useState<ColaboradoresProps[]>(
    []
  );
  const [valueSearch, setValueSearch] = useState('');
  const [listEmpty, setListEmpty] = useState('');

  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');
  const [usarFiltroData, setUsarFiltroData] = useState(false);
  const [gerandoExcel, setGerandoExcel] = useState(false);

  const [page, setPage] = useState(1);
  const PER_PAGE = 30;

  // Estado para alertas modernos
  const [alertConfig, setAlertConfig] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showAlert = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setAlertConfig({ open: true, message, severity });
  };

  const handleCloseAlert = () => {
    setAlertConfig({ ...alertConfig, open: false });
  };

  const count = Math.ceil(dataUser.length / PER_PAGE);
  const _DATA = usePagination(dataUser, PER_PAGE);

  const filterContractsPerPage = useMemo(() => {
    const currentData = _DATA.currentData();
    if (currentData.length === 0) return [];
    
    const searchUpper = search.toUpperCase();
    return currentData.filter(
      (contrato: ColaboradoresProps) =>
        (contrato.cnpj?.toUpperCase().includes(searchUpper)) ||
        (contrato?.razao_social?.toUpperCase().includes(searchUpper)) ||
        (contrato?.nome_completo?.toUpperCase().includes(searchUpper)) ||
        (contrato?.cpf_colaborador?.toUpperCase().includes(searchUpper))
    );
  }, [_DATA, search]);

  const filter = filterContracts.length > 0 ? filterContracts : filterContractsPerPage;

  const rows = useMemo(() => {
    return filter.map((colab) => {
      return createData(
        colab.razao_social,
        colab.cpf_colaborador,
        colab.nome_completo,
        formatDateBr(colab.data_ultimo_agendamento),
        colab.cnpj,
        colab.razao_social,
        colab.situacao,
        colab.ghe,
        colab.local_obra
      );
    });
  }, [filter]);

  const fetchFilter = useCallback(
    async (termo: string, situacao: string) => {
      try {
        setSearch(termo); 

        const data = {
          nome: termo.toUpperCase().trim(),
          situacao: situacao.toUpperCase().trim()
        };

        const response = await api.post('ravim/pesq-exame', data);
        setFilterContracts(response.data);
      } catch (error: any) {
        const backendMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao realizar pesquisa';
        const backendCode = error.response?.data?.code || error.response?.status;
        
        if (backendCode === 404) {
          setFilterContracts([]);
          setListEmpty(backendMessage);
        } else {
          showAlert(backendMessage, 'error');
        }
      }
    },
    []
  );

  async function fetchListAgendamentos() {
    try {
      setIsLoading(true);
      const response = await api.get('/ravim/exames-vencer');
      if (response.data.estado === 404) return;
      setDataUser(response.data);
      setCnpjExcel(response.data);
    } catch (error: any) {
      console.log(error);
      const backendMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao carregar agendamentos';
      showAlert(backendMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  }

  function downloadExcel(dados: any[], nomeArquivo: string) {
    if (!dados || dados.length === 0) {
      showAlert('Nenhum dado para exportar!', 'warning');
      return;
    }

    const planilha = dados.map((item) => ({
      'CNPJ': item.cnpj || '',
      'Razão Social': item.razao_social || '',
      'Nome do Colaborador': item.nome_completo || '',
      'CPF': item.cpf_colaborador || '',
      'Data de Nascimento': item.data_nascimento ? formatDateBr(item.data_nascimento) : '',
      'RG': item.rg || '',
      'Função': item.funcao || '',
      'Setor': item.setor || '',
      'E-mail': item.email || '',
      'Telefone': formatTelefone(item.telefone),
      'Status do Colaborador': item.status_colaborador || '',
      'Status do Agendamento': item.status_agendamento || '',
      'Tipo de Aso': item.tipo_aso || '',
      'Exame': item.nome_exame || '',
      'Data do Agendamento': item.data_agendamento ? formatDateBr(item.data_agendamento) : '',
    }));

    const ws = XLSX.utils.json_to_sheet(planilha);

    const colWidths = [
      { wch: 18 }, { wch: 35 }, { wch: 35 }, { wch: 15 }, { wch: 15 },
      { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 30 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 25 }, { wch: 18 }, { wch: 18 }, { wch: 18 }
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Exames a Vencer');
    XLSX.writeFile(wb, `${nomeArquivo}.xlsx`);
  }

  async function gerarExcel() {
    const cnpjParaEnviar = selectedCnpjValue?.cnpj || selectedCnpjValue;

    if (!cnpjParaEnviar) {
      showAlert('Por favor, selecione uma empresa primeiro!', 'warning');
      return;
    }

    try {
      setGerandoExcel(true);

      const data: any = {
        cnpj: cnpjParaEnviar
      };

      if (usarFiltroData && dataInicio && dataFim) {
        data.data_inicio = dataInicio;
        data.data_fim = dataFim;
      }

      const response = await api.post('/ravim/excel-exames-vencer', data);

      if (response.data.code === 404) {
        showAlert(response.data.message || 'Nenhum dado encontrado para o período selecionado', 'warning');
        return;
      }

      let dadosParaExportar = [];
      let totalRegistros = 0;

      if (response.data.resultados) {
        dadosParaExportar = response.data.resultados;
        totalRegistros = response.data.total_registros || response.data.resultados.length;
      } else if (Array.isArray(response.data)) {
        dadosParaExportar = response.data;
        totalRegistros = response.data.length;
      }

      if (dadosParaExportar.length === 0) {
        showAlert('Nenhum registro encontrado para os critérios selecionados!', 'warning');
        return;
      }

      let nomeArquivo = 'exames_vencer';
      
      if (nomeEmpresa && nomeEmpresa !== 'undefined') {
        nomeArquivo = nomeEmpresa;
      } else if (selectedCnpjValue?.razao_social && selectedCnpjValue?.razao_social !== 'undefined') {
        nomeArquivo = selectedCnpjValue.razao_social;
      }
      
      if (usarFiltroData && dataInicio && dataFim) {
        const dataInicioFormatada = formatDateBr(dataInicio).replace(/\//g, '-');
        const dataFimFormatada = formatDateBr(dataFim).replace(/\//g, '-');
        nomeArquivo += `_${dataInicioFormatada}_a_${dataFimFormatada}`;
      }
      
      nomeArquivo = nomeArquivo.replace(/[\\/:*?"<>|]/g, '_');
      
      downloadExcel(dadosParaExportar, nomeArquivo);
      showAlert(`Excel gerado com sucesso! ${totalRegistros} registros exportados.`, 'success');
    } catch (error: any) {
      console.log(error);
      const backendMessage = error.message || error.response?.data?.error || 'Erro ao gerar Excel';
      showAlert(backendMessage, 'error');
    } finally {
      setGerandoExcel(false);
    }
  }

  const handleSelectCnpj = (cnpjObj: any) => {
    if (!cnpjObj) {
      setSelectedCnpjValue(null);
      setNomeEmpresa('');
      return;
    }
    
    if (typeof cnpjObj === 'object') {
      setSelectedCnpjValue(cnpjObj);
      const nome = cnpjObj.razao_social || cnpjObj.nome_empresa || cnpjObj.nome || '';
      setNomeEmpresa(nome);
    } else if (typeof cnpjObj === 'string') {
      setSelectedCnpjValue({ cnpj: cnpjObj });
      setNomeEmpresa('');
    }
  };

  const handleClearCnpj = () => {
    setSelectedCnpjValue(null);
    setNomeEmpresa('');
  };

  const handleChangeRazaoSocial = (name: string) => {
    setNomeEmpresa(name);
  };

  function handleApplyDateFilter() {
    if (!dataInicio || !dataFim) {
      showAlert('Por favor, selecione a data de início e a data de fim', 'warning');
      return;
    }

    if (new Date(dataInicio) > new Date(dataFim)) {
      showAlert('A data de início não pode ser maior que a data de fim', 'warning');
      return;
    }

    setUsarFiltroData(true);
    showAlert(`Filtro aplicado: ${formatDateBr(dataInicio)} até ${formatDateBr(dataFim)}`, 'success');
  }

  function handleClearDateFilter() {
    setDataInicio('');
    setDataFim('');
    setUsarFiltroData(false);
  }

  const handleChange = (e: any, p: number) => {
    setPage(p);
    _DATA.jump(p);
  };

  useEffect(() => {
    fetchListAgendamentos();
  }, []);

  function handleClearFields() {
    setSearch('');
    setValueSearch('');
    setSituacao('');
    setListEmpty('');
    setFilterContracts([]);
    handleClearDateFilter();
    handleClearCnpj();
  }

  const uniqueCnpj = useMemo(() => {
    return cnpjExcel.filter((item, index) => {
      return index === cnpjExcel.findIndex((cnpj) => item.cnpj === cnpj.cnpj);
    });
  }, [cnpjExcel]);

  const podeGerarExcel = selectedCnpjValue && selectedCnpjValue.cnpj && !gerandoExcel;

  return (
    <Container maxWidth="xl" style={{ padding: '20px' }}>
      {/* SNACKBAR PARA ALERTAS MELHORADOS */}
      <Snackbar 
        open={alertConfig.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseAlert} severity={alertConfig.severity} variant="filled">
          {alertConfig.message}
        </Alert>
      </Snackbar>

      {/* LINHA DE FILTROS */}
      <div
        style={{
          display: 'flex',
          gap: '20px',
          marginBottom: '20px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}
      >
        <TextField
          value={valueSearch}
          onChange={(e) => {
            setValueSearch(e.target.value);
          }}
          label="Pesquisar empresas"
          type="text"
          variant="outlined"
          size="small"
          style={{ background: '#FFF', flex: 1, minWidth: '200px' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              fetchFilter(valueSearch, situacao);
            }
          }}
        />

        <FormControl variant="outlined" size="small" style={{ minWidth: '200px' }}>
          <InputLabel>Situação</InputLabel>
          <Select
            label="Situação"
            value={situacao}
            onChange={(e: any) => {
              setSituacao(e.target.value);
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            {situ &&
              situ.map((situacao) => (
                <MenuItem value={situacao.value} key={situacao.id}>
                  {situacao.situacao}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <MuiButton
          variant="contained"
          className={classes.secondaryButton}
          startIcon={<MagnifyingGlass size={20} />}
          onClick={() => {
            fetchFilter(valueSearch, situacao);
          }}
        >
          Pesquisar
        </MuiButton>

        {(filterContracts.length > 0 || !!listEmpty) && (
          <MuiButton
            variant="contained"
            className={classes.redButton}
            startIcon={<X size={20} />}
            onClick={handleClearFields}
          >
            Limpar Filtros
          </MuiButton>
        )}
      </div>

      {/* FILTRO DE DATAS */}
      <Paper style={{ padding: '20px', marginBottom: '20px', backgroundColor: '#e8f4f8' }}>
        <Typography variant="h6" gutterBottom style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Calendar size={24} />
          Filtrar por Período (Opcional)
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Selecione um período para filtrar os exames. Deixe em branco para exportar todos.
        </Typography>

        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 15 }}>
          <TextField
            label="Data Início"
            type="date"
            value={dataInicio}
            onChange={(e) => {
              setDataInicio(e.target.value);
              if (usarFiltroData) setUsarFiltroData(false);
            }}
            InputLabelProps={{ shrink: true }}
            size="small"
            style={{ minWidth: '200px' }}
          />

          <TextField
            label="Data Fim"
            type="date"
            value={dataFim}
            onChange={(e) => {
              setDataFim(e.target.value);
              if (usarFiltroData) setUsarFiltroData(false);
            }}
            InputLabelProps={{ shrink: true }}
            size="small"
            style={{ minWidth: '200px' }}
          />

          <MuiButton
            variant="contained"
            className={classes.primaryButton}
            onClick={handleApplyDateFilter}
            disabled={!dataInicio || !dataFim}
          >
            Aplicar Filtro
          </MuiButton>

          {usarFiltroData && (
            <MuiButton
              variant="outlined"
              className={classes.secondaryButton}
              onClick={handleClearDateFilter}
            >
              Limpar Filtro
            </MuiButton>
          )}
        </div>

        {usarFiltroData && dataInicio && dataFim && (
          <Typography variant="body2" style={{ marginTop: 15, padding: 10, backgroundColor: '#d4edda', borderRadius: 5, color: '#155724' }}>
            ✅ Filtrando exames entre {formatDateBr(dataInicio)} e {formatDateBr(dataFim)}
          </Typography>
        )}
      </Paper>

      {/* TÍTULO */}
      <Typography variant="h4" align="center" style={{ marginBottom: 20, fontWeight: 'bold' }}>
        Exames a Vencer
      </Typography>

      {/* BARRA DE AÇÕES */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 10
        }}
      >
        <Typography variant="subtitle1">
          Total: {dataUser.length} colaboradores
        </Typography>

        <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <SafeAutocompleteCnpj
            cnpjExcel={uniqueCnpj}
            selectedCnpj={selectedCnpjValue}
            onChangeSelectedCnpj={handleSelectCnpj}
            onChangeRazaoSocial={handleChangeRazaoSocial}
          />

          <MuiButton
            variant="contained"
            className={classes.successButton}
            startIcon={<Download size={20} />}
            onClick={gerarExcel}
            disabled={!podeGerarExcel}
          >
            {gerandoExcel ? 'Gerando Excel...' : 'Gerar Excel'}
          </MuiButton>
        </div>
      </div>

      {/* INFO DA EMPRESA SELECIONADA */}
      {selectedCnpjValue && selectedCnpjValue.cnpj && (
        <Paper style={{ padding: 10, marginBottom: 20, backgroundColor: '#d4edda' }}>
          <Typography align="center" style={{ color: '#155724' }}>
            ✅ Empresa selecionada: {selectedCnpjValue.razao_social || selectedCnpjValue.nome_empresa || selectedCnpjValue.cnpj} (CNPJ: {selectedCnpjValue.cnpj})
          </Typography>
        </Paper>
      )}

      {/* AVISO DE CNPJ NÃO SELECIONADO */}
      {!selectedCnpjValue && (
        <Paper style={{ padding: 10, marginBottom: 20, backgroundColor: '#fff3cd' }}>
          <Typography align="center" style={{ color: '#856404' }}>
            ⚠️ Selecione uma empresa acima para gerar o relatório em Excel
          </Typography>
        </Paper>
      )}

      {/* TABELA DE RESULTADOS */}
      {!listEmpty ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          <Pagination
            count={count}
            size="large"
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={handleChange}
          />

          <TableContainer component={Paper}>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>CNPJ</TableCell>
                  <TableCell align="right">Razão Social</TableCell>
                  <TableCell align="right">CPF</TableCell>
                  <TableCell align="right">Nome Completo</TableCell>
                  <TableCell align="right">Situação</TableCell>
                  <TableCell align="right">Data do último ASO</TableCell>
                  <TableCell align="right">GHE</TableCell>
                  <TableCell align="right">Local/Obra</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <Row key={index} row={row} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Pagination
            count={count}
            size="large"
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={handleChange}
          />
        </div>
      ) : (
        <Paper style={{ padding: 40, textAlign: 'center' }}>
          <Typography variant="h5" color="textSecondary">
            {listEmpty}
          </Typography>
        </Paper>
      )}

      {/* LOADING */}
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <CircularProgress size={40} />
        </div>
      )}
    </Container>
  );
}
